
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import PageHero from '../../components/feature/PageHero';

// Canonical pricing — source of truth:
// /Users/minihive/HIVE-KNOWLEDGE/00-command/CANONICAL-PRICING-2026-04-17.md
// /Users/minihive/HIVE-AGENTS/CANONICAL-STRIPE-LINKS.md
const STRIPE_LINKS = {
  T5_RECEPTIONIST_SETUP: 'https://buy.stripe.com/00wcN55yVgX37MVgXmgfu2j',   // $1,497 one-time
  T7_RECEPTIONIST_MONTHLY: 'https://buy.stripe.com/4gMfZhf9v9uB1ox4aAgfu2k', // $297/mo
  T9_DFY_SECURITY: 'https://buy.stripe.com/eVq00jgdzcGN0kt6iIgfu2n',          // $2,497 one-time
  T10_STARTER: 'https://buy.stripe.com/28EaEX4uRdKR3wFcH6gfu2o',              // $2,500/mo
  T10_GROWTH: 'https://buy.stripe.com/aFa14n4uR0Y5ffndLagfu2p',               // $5,000/mo
  T10_DOMINANCE: 'https://buy.stripe.com/cNidR9e5r5el5EN22sgfu2q',            // $10,000/mo
};

export default function ServicesPage() {
  const packages = [
    {
      name: 'Starter',
      description: 'Perfect for businesses just beginning their AI marketing journey',
      price: 'Starting at $2,500/mo',
      features: [
        'Single pillar focus (choose one)',
        'Basic AI visibility tracking',
        'Monthly reporting',
        'Email support',
        'Quarterly strategy reviews'
      ],
      bgColor: 'bg-teal-500',
      cta: 'Get Started',
      href: STRIPE_LINKS.T10_STARTER,
      external: true
    },
    {
      name: 'Growth',
      description: 'For businesses ready to scale with integrated AI marketing',
      price: 'Starting at $5,000/mo',
      features: [
        'Two pillars + integration',
        'Advanced AI visibility tracking',
        'Bi-weekly reporting',
        'Priority support',
        'Monthly strategy sessions',
        'Competitive monitoring',
        'Landing page optimization'
      ],
      bgColor: 'bg-teal-500',
      cta: 'Scale Up',
      popular: true,
      href: STRIPE_LINKS.T10_GROWTH,
      external: true
    },
    {
      name: 'Dominance',
      description: 'Complete AI marketing system for market leaders',
      price: 'Starting at $10,000/mo',
      features: [
        'All three pillars fully integrated',
        'Real-time AI visibility dashboard',
        'Weekly reporting & optimization',
        'Dedicated account manager',
        'Weekly strategy calls',
        'Full competitive intelligence',
        'Custom landing pages',
        'Priority early access to new AI ad placements'
      ],
      bgColor: 'bg-teal-500',
      cta: 'Dominate',
      href: STRIPE_LINKS.T10_DOMINANCE,
      external: true
    }
  ];

  const addOns = [
    {
      icon: 'ri-code-s-slash-line',
      title: 'Schema Sprint',
      description: 'Complete schema markup implementation across your site',
      price: '$1,500 one-time',
      href: '/contact',
      external: false
    },
    {
      icon: 'ri-layout-line',
      title: 'Landing Page Rebuild',
      description: 'AI-optimized landing pages designed for conversational traffic',
      price: '$3,000 per page',
      href: '/contact',
      external: false
    },
    {
      // Canonical T5 + T7 combo — replaces old $2,500 setup + $500/mo
      icon: 'ri-customer-service-2-line',
      title: 'AI Receptionist (Voice Agent) — Setup + Monthly',
      description: 'Custom AI voice agent with CRM integration. 7-day booking conversion setup, then recurring monthly management.',
      price: '$1,497 setup + $297/mo',
      href: STRIPE_LINKS.T5_RECEPTIONIST_SETUP,
      external: true,
      secondary: { label: 'Subscribe monthly', href: STRIPE_LINKS.T7_RECEPTIONIST_MONTHLY }
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Competitive Intelligence',
      description: 'Monthly deep-dive into competitor AI visibility',
      price: '$1,000/mo add-on',
      href: '/contact',
      external: false
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      {/* Hero */}
      <PageHero
        eyebrow="AI Marketing Services"
        title="AI Marketing Services for Florida Businesses"
        subtitle="Choose the package that fits your goals — or build a custom solution. Every plan includes transparent reporting and measurable results."
      />

      {/* Packages */}
      <section className="px-6 lg:px-12 py-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-display text-navy-900 mb-6">
              Choose Your Package
            </h2>
            <p className="text-xl text-navy-600">
              Flexible plans designed to grow with your business
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <div
                key={index}
                className={`relative bg-white rounded-2xl shadow-xl p-8 ${
                  pkg.popular ? 'ring-4 ring-teal-500 scale-105' : ''
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-teal-500 text-navy-900 text-sm font-semibold rounded-full">
                    Most Popular
                  </div>
                )}

                <div className={`w-16 h-16 ${pkg.bgColor} rounded-xl flex items-center justify-center mb-6`}>
                  <i className="ri-vip-crown-line text-3xl text-navy-900"></i>
                </div>

                <h3 className="text-2xl font-bold font-display text-navy-900 mb-2">
                  {pkg.name}
                </h3>
                <p className="text-sm text-navy-600 mb-4">
                  {pkg.description}
                </p>
                <div className="text-3xl font-bold text-navy-900 mb-6">
                  {pkg.price}
                </div>

                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <i className="ri-check-line text-lg text-teal-600 mt-0.5"></i>
                      <span className="text-sm text-navy-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                {pkg.external ? (
                  <a
                    href={pkg.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`block w-full px-6 py-3 ${pkg.bgColor} text-navy-900 text-center font-semibold rounded-lg hover:shadow-lg transition-all whitespace-nowrap cursor-pointer`}
                  >
                    {pkg.cta}
                  </a>
                ) : (
                  <Link
                    to={pkg.href || '/contact'}
                    className={`block w-full px-6 py-3 ${pkg.bgColor} text-navy-900 text-center font-semibold rounded-lg hover:shadow-lg transition-all whitespace-nowrap cursor-pointer`}
                  >
                    {pkg.cta}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* T9 DFY Enterprise Security Stack — $2,497 one-time */}
      <section className="px-6 lg:px-12 py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-5 gap-10 items-center">
            <div className="md:col-span-3">
              <span className="inline-block bg-teal-500 text-navy-900 px-4 py-1 rounded-full text-sm font-semibold mb-4">
                DONE-FOR-YOU INSTALL
              </span>
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-4">
                Enterprise Security Stack + Full AI Deployment
              </h2>
              <p className="text-lg text-navy-100 mb-6">
                We install, harden, and launch your full AI stack — secure agents, CRM wiring, call recording, compliance guardrails, CRM + Stripe integration, and custom guardrails. Engineered for contractors handling sensitive customer data.
              </p>
              <ul className="space-y-2 mb-8">
                <li className="flex items-start gap-3"><i className="ri-check-line text-orange-400 text-lg mt-0.5"></i><span className="text-sm text-navy-100">End-to-end agent install, hardened for PCI / PHI exposure</span></li>
                <li className="flex items-start gap-3"><i className="ri-check-line text-orange-400 text-lg mt-0.5"></i><span className="text-sm text-navy-100">Secure voice + inbox + CRM + payments integration</span></li>
                <li className="flex items-start gap-3"><i className="ri-check-line text-orange-400 text-lg mt-0.5"></i><span className="text-sm text-navy-100">Compliance guardrails, prompt-injection defenses, audit logging</span></li>
                <li className="flex items-start gap-3"><i className="ri-check-line text-orange-400 text-lg mt-0.5"></i><span className="text-sm text-navy-100">14-day implementation SLA with handoff docs</span></li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <div className="text-4xl font-bold">$2,497 <span className="text-base text-navy-200 font-normal">one-time</span></div>
                <a
                  href={STRIPE_LINKS.T9_DFY_SECURITY}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-block px-8 py-3 whitespace-nowrap"
                >
                  Install My Stack — $2,497
                </a>
              </div>
            </div>
            <div className="md:col-span-2 flex justify-center">
              <div className="w-48 h-48 bg-teal-50 border-2 border-teal-500 rounded-full flex items-center justify-center">
                <i className="ri-shield-keyhole-line text-8xl text-orange-400"></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Add-ons */}
      <section className="px-6 lg:px-12 py-20 bg-navy-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-display text-navy-900 mb-6">
              Add-On Modules
            </h2>
            <p className="text-xl text-navy-600">
              Enhance any package with specialized services
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {addOns.map((addon, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${addon.icon} text-xl text-teal-600`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold font-display text-navy-900 mb-2">
                      {addon.title}
                    </h3>
                    <p className="text-sm text-navy-600 mb-3">
                      {addon.description}
                    </p>
                    <p className="text-sm font-semibold text-teal-600 mb-4">
                      {addon.price}
                    </p>
                    <div className="flex flex-wrap gap-3">
                      {addon.external ? (
                        <a
                          href={addon.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-primary inline-block px-4 py-2 text-sm whitespace-nowrap"
                        >
                          Get Setup
                        </a>
                      ) : (
                        <Link
                          to={addon.href}
                          className="btn-primary inline-block px-4 py-2 text-sm whitespace-nowrap"
                        >
                          Request Quote
                        </Link>
                      )}
                      {addon.secondary && (
                        <a
                          href={addon.secondary.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block px-4 py-2 bg-navy-100 hover:bg-navy-200 text-navy-900 text-sm font-semibold rounded-lg transition-all whitespace-nowrap"
                        >
                          {addon.secondary.label}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Three Pillars Overview */}
      <section className="px-6 lg:px-12 py-20 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-display text-navy-900 mb-6">
              Our Three Pillars
            </h2>
            <p className="text-xl text-navy-600">
              The complete AI marketing system
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            <Link to="/chatgpt-ads" className="group bg-navy-50 rounded-2xl p-8 hover:shadow-xl transition-all cursor-pointer">
              <div className="w-16 h-16 bg-teal-50 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-advertisement-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold font-display text-navy-900 mb-3">
                ChatGPT Ads Readiness
              </h3>
              <p className="text-navy-700 mb-4">
                Prepare for conversational ad placements as they roll out across AI platforms.
              </p>
              <span className="text-teal-600 font-semibold flex items-center gap-2">
                Learn More <i className="ri-arrow-right-line"></i>
              </span>
            </Link>

            <Link to="/geo" className="group bg-navy-50 rounded-2xl p-8 hover:shadow-xl transition-all cursor-pointer">
              <div className="w-16 h-16 bg-teal-50 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-search-eye-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold font-display text-navy-900 mb-3">
                GEO / AI Search Visibility
              </h3>
              <p className="text-navy-700 mb-4">
                Get your brand mentioned and recommended by AI assistants across all platforms.
              </p>
              <span className="text-teal-600 font-semibold flex items-center gap-2">
                Learn More <i className="ri-arrow-right-line"></i>
              </span>
            </Link>

            <Link to="/voice-ai" className="group bg-navy-50 rounded-2xl p-8 hover:shadow-xl transition-all cursor-pointer">
              <div className="w-16 h-16 bg-teal-50 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-customer-service-2-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold font-display text-navy-900 mb-3">
                AI Voice Agents
              </h3>
              <p className="text-navy-700 mb-4">
                Never miss a lead with 24/7 AI-powered call answering and appointment booking.
              </p>
              <span className="text-teal-600 font-semibold flex items-center gap-2">
                Learn More <i className="ri-arrow-right-line"></i>
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 lg:px-12 py-16 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-display text-white mb-6">
            Not Sure Which Package Is Right?
          </h2>
          <p className="text-xl text-navy-100 mb-8">
            Get a free Assessment and we'll recommend the perfect starting point for your business.
          </p>
          <Link
            to="/audit"
            className="btn-primary inline-block px-10 py-4 text-lg whitespace-nowrap cursor-pointer"
          >
            Get Free AI Assessment
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
